import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Barcode Scanner Test Page
 *
 * Tests XB-6208-RB Bluetooth barcode scanner integration.
 * Scanner behavior: HID Keyboard mode, Tab suffix, no prefix, instant speed.
 */

interface ScanEvent {
  id: number;
  timestamp: Date;
  value: string;
  inputTime: number;
  charCount: number;
  detectionMethod: 'tab' | 'enter' | 'speed' | 'manual';
}

const BarcodeTest: React.FC = () => {
  const [scannedValues, setScannedValues] = useState<ScanEvent[]>([]);
  const [currentInput, setCurrentInput] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [speedThreshold, setSpeedThreshold] = useState(50);

  const inputRef = useRef<HTMLInputElement>(null);
  const timestampRef = useRef<number>(0);
  const firstCharTimeRef = useRef<number>(0);
  const eventIdRef = useRef<number>(0);

  // Print scanned codes
  const handlePrint = () => {
    if (scannedValues.length === 0) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Scanned Barcodes - ${new Date().toLocaleDateString()}</title>
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            padding: 20px;
            max-width: 800px;
            margin: 0 auto;
          }
          h1 {
            font-size: 18px;
            border-bottom: 2px solid #333;
            padding-bottom: 10px;
            margin-bottom: 20px;
          }
          .scan-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 0;
            border-bottom: 1px solid #eee;
          }
          .scan-value {
            font-family: 'Courier New', monospace;
            font-size: 16px;
            font-weight: bold;
          }
          .scan-meta {
            font-size: 12px;
            color: #666;
          }
          .summary {
            margin-top: 20px;
            padding-top: 10px;
            border-top: 2px solid #333;
            font-size: 14px;
            color: #666;
          }
          @media print {
            body { padding: 0; }
          }
        </style>
      </head>
      <body>
        <h1>Scanned Barcodes</h1>
        <div class="scans">
          ${scannedValues.map((scan, index) => `
            <div class="scan-item">
              <span class="scan-value">${index + 1}. ${scan.value}</span>
              <span class="scan-meta">${scan.timestamp.toLocaleTimeString()} | ${scan.charCount} chars</span>
            </div>
          `).join('')}
        </div>
        <div class="summary">
          Total: ${scannedValues.length} codes | Printed: ${new Date().toLocaleString()}
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const recordScan = useCallback((value: string, inputTime: number, method: ScanEvent['detectionMethod']) => {
    if (value.trim().length === 0) return;

    const event: ScanEvent = {
      id: ++eventIdRef.current,
      timestamp: new Date(),
      value: value.trim(),
      inputTime,
      charCount: value.length,
      detectionMethod: method,
    };

    setScannedValues(prev => [event, ...prev].slice(0, 50));
    setCurrentInput('');
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const now = Date.now();
    const value = e.target.value;

    if (currentInput.length === 0 && value.length > 0) {
      firstCharTimeRef.current = now;
    }

    timestampRef.current = now;
    setCurrentInput(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Tab key - XB-6208-RB sends Tab after barcode
    if (e.key === 'Tab' && currentInput.length > 0) {
      e.preventDefault();
      const inputDuration = timestampRef.current - firstCharTimeRef.current;
      recordScan(currentInput, inputDuration, 'tab');
      inputRef.current?.focus();
      return;
    }

    // Enter key - fallback for other scanners
    if (e.key === 'Enter' && currentInput.length > 0) {
      e.preventDefault();
      const inputDuration = timestampRef.current - firstCharTimeRef.current;
      recordScan(currentInput, inputDuration, 'enter');
      return;
    }
  };

  // Speed-based detection fallback (for scanners without suffix)
  useEffect(() => {
    if (!isListening || currentInput.length < 4) return;

    const timer = setTimeout(() => {
      const inputDuration = timestampRef.current - firstCharTimeRef.current;
      const avgTimePerChar = inputDuration / currentInput.length;

      if (avgTimePerChar < speedThreshold) {
        recordScan(currentInput, inputDuration, 'speed');
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [currentInput, isListening, speedThreshold, recordScan]);

  const handleManualSubmit = () => {
    if (currentInput.length > 0) {
      const inputDuration = timestampRef.current - firstCharTimeRef.current;
      recordScan(currentInput, inputDuration, 'manual');
    }
  };

  const startListening = () => {
    setIsListening(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const stopListening = () => {
    setIsListening(false);
  };

  const clearHistory = () => {
    setScannedValues([]);
  };

  const copyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Barcode Scanner Test
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          XB-6208-RB Bluetooth Scanner (Tab suffix mode)
        </p>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h2 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">Test Instructions:</h2>
        <ol className="list-decimal list-inside text-blue-700 dark:text-blue-300 space-y-1 text-sm">
          <li>Ensure scanner is paired via Bluetooth</li>
          <li>Click "Start Listening" to focus the input field</li>
          <li>Scan a barcode - it should appear and auto-submit on Tab</li>
          <li>Check timing info to verify scanner behavior</li>
        </ol>
      </div>

      {/* Scanner Input Area */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={isListening ? stopListening : startListening}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              isListening
                ? 'bg-red-500 hover:bg-red-600 text-white'
                : 'bg-green-500 hover:bg-green-600 text-white'
            }`}
          >
            {isListening ? '⏹ Stop' : '▶ Start Listening'}
          </button>

          <div className={`flex items-center gap-2 ${isListening ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
            <span className={`w-3 h-3 rounded-full ${isListening ? 'bg-green-500 animate-pulse' : 'bg-gray-300 dark:bg-gray-600'}`}></span>
            <span className="text-sm font-medium">
              {isListening ? 'Ready to scan...' : 'Not listening'}
            </span>
          </div>
        </div>

        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={currentInput}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={isListening ? "Scan a barcode now..." : "Click 'Start Listening' first"}
            disabled={!isListening}
            className={`w-full px-4 py-4 text-xl font-mono border-2 rounded-lg transition-colors
              ${isListening
                ? 'border-green-500 bg-green-50 dark:bg-green-900/20 focus:ring-2 focus:ring-green-500 dark:text-white'
                : 'border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400'}
            `}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />

          {currentInput && (
            <button
              onClick={handleManualSubmit}
              className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            >
              Submit
            </button>
          )}
        </div>

        {/* Config */}
        <div className="mt-4 flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
          <label className="flex items-center gap-2">
            Speed threshold (ms/char):
            <input
              type="number"
              value={speedThreshold}
              onChange={(e) => setSpeedThreshold(Number(e.target.value))}
              className="w-16 px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              min={10}
              max={200}
            />
          </label>
          <span className="text-xs">(Human ~200-400ms, Scanner ~10-50ms)</span>
        </div>
      </div>

      {/* Scan History */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Scan History ({scannedValues.length})
          </h2>
          <div className="flex gap-2">
            <button
              onClick={handlePrint}
              disabled={scannedValues.length === 0}
              className="px-3 py-1 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print
            </button>
            <button
              onClick={clearHistory}
              className="px-3 py-1 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
            >
              Clear All
            </button>
          </div>
        </div>

        {scannedValues.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <div className="text-4xl mb-2">📷</div>
            <p>No scans yet. Start scanning barcodes!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {scannedValues.map((scan) => (
              <div
                key={scan.id}
                className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-lg text-gray-900 dark:text-white break-all">
                    {scan.value}
                  </div>
                  <div className="flex flex-wrap gap-2 sm:gap-4 text-xs text-gray-500 dark:text-gray-400 mt-1">
                    <span>Len: {scan.charCount}</span>
                    <span>Time: {scan.inputTime}ms</span>
                    <span>Avg: {scan.charCount > 0 ? (scan.inputTime / scan.charCount).toFixed(1) : 0}ms/char</span>
                    <span className={`px-2 py-0.5 rounded ${
                      scan.detectionMethod === 'tab' ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300' :
                      scan.detectionMethod === 'enter' ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' :
                      scan.detectionMethod === 'speed' ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300' :
                      'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                    }`}>
                      {scan.detectionMethod}
                    </span>
                    <span>{scan.timestamp.toLocaleTimeString()}</span>
                  </div>
                </div>
                <button
                  onClick={() => copyToClipboard(scan.value)}
                  className="flex-shrink-0 px-3 py-1 text-sm bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 rounded"
                >
                  Copy
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Scanner Info */}
      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">Expected Scanner Behavior:</h3>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
          <li>• <strong>Suffix:</strong> Tab key (prevents focus change)</li>
          <li>• <strong>Prefix:</strong> None</li>
          <li>• <strong>Speed:</strong> Instant (&lt;50ms per character)</li>
          <li>• <strong>Connection:</strong> Bluetooth only (USB not working)</li>
        </ul>
      </div>
    </div>
  );
};

export default BarcodeTest;
